"use client";
import { Select, Space, Spin, Typography } from "antd/lib";
import type { FC } from "react";
import React, { useMemo, useState } from "react";
import { Line } from "@ant-design/plots";
import { useTranslations } from "next-intl";
import { useCurrencyContext } from "@/context/CurrencyContext";
import useAxiosQuery from "@/hooks/useMyQuery";
import { formatThousands } from "@/helpers";
import MyCard from "@/app/components/MyCard";
import Heading from "@/app/components/Heading";
import LastUpdated from "@/app/components/LastUpdated";
import { DateTimeFormat } from "@/constant";
import dayjs from "@/utils/dayjsConfig";

type Data = {
  mean: number;
  lower: number;
  upper: number;
  date: string;
};

type Duration = "15M" | "30M" | "1H" | "4H" | "6H" | "12H" | "1D";

const durations: Duration[] = ["15M", "30M", "1H", "4H", "6H", "12H", "1D"];

const boundColor = "#ffb0b0";

const PriceForecast: FC<{ symbol: string }> = ({ symbol }) => {
  const [duration, setDuration] = useState<Duration>("15M");
  const t = useTranslations("products");
  const { currentCurrency } = useCurrencyContext();

  const {
    data: fetchedData,
    fetchTime,
    isLoading,
  } = useAxiosQuery<{ data?: { prediction: Data[]; true: number[] } }>(
    {
      url: "v1/product/forecast/price",
      params: { symbol, duration },
    },
    { queryKey: ["v1/product/forecast/price", symbol, duration] },
  );

  const storedTimes = useMemo(() => fetchedData?.data?.prediction?.map(c => c.date) ?? [], [fetchedData]);

  const realPriceData = useMemo(
    () =>
      fetchedData?.data?.true?.map((value, index) => ({
        date: storedTimes[index],
        name: "Real Price",
        value,
      })) || [],
    [fetchedData?.data?.true, storedTimes],
  );

  const predictedData = useMemo(
    () =>
      fetchedData?.data?.prediction?.map(c => ({
        date: c.date,
        name: "Predicted Price",
        value: c.mean,
      })) || [],
    [fetchedData],
  );

  const lowerBoundData = useMemo(
    () =>
      fetchedData?.data?.prediction?.map(c => ({
        date: c.date,
        name: "Lower Bound",
        value: c.lower,
      })) || [],
    [fetchedData],
  );

  const upperBoundData = useMemo(
    () =>
      fetchedData?.data?.prediction?.map(c => ({
        date: c.date,
        name: "Upper Bound",
        value: c.upper,
      })) || [],
    [fetchedData],
  );
  const result = useMemo(
    () =>
      fetchedData?.data?.prediction?.map(c => ({
        date: c.date,
        name: "95% Confidence Interval",
        value: c.upper - c.lower,
      })) || [],
    [fetchedData],
  );

  const { combinedData, yMin, yMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    const data = [...realPriceData, ...predictedData, ...lowerBoundData, ...upperBoundData].map(item => {
      min = Math.min(min, item.value);
      max = Math.max(max, item.value);
      return item;
    });

    return {
      combinedData: data.concat(result),
      yMin: min * 0.99,
      yMax: max * 1.01,
    };
  }, [realPriceData, predictedData, lowerBoundData, upperBoundData, result]);

  const chartConfig = useMemo(
    () => ({
      data: combinedData,
      xField: "date",
      yField: "value",
      seriesField: "name",
      legend: {
        position: "top-right",
      },
      tooltip: {
        showCrosshairs: true,
        shared: true,
        title: (_: any, item: { date: string | number | dayjs.Dayjs | Date | null | undefined }) =>
          dayjs(item.date).format(DateTimeFormat),
        formatter: (item: { name: any; value: string | number }) => ({
          name: item.name,
          value: formatThousands(+item.value, currentCurrency),
        }),
      },
      xAxis: {
        boundaryGap: false,
        scale: true,
        axisLine: { onZero: false },
        splitLine: { show: false },
        label: {
          formatter: (_: any, item: { name: string | number | dayjs.Dayjs | Date | null | undefined }) =>
            dayjs(item.name).format("DD-HH:mm"),
        },
      },
      yAxis: {
        position: "right",
        min: yMin,
        max: yMax,
        label: {
          formatter: (v: string | number) => formatThousands(+v, currentCurrency),
        },
      },
      annotations: [
        {
          type: "line",
          start: [storedTimes?.[storedTimes.length - 21], "min"],
          end: [storedTimes?.[storedTimes.length - 21], "max"],
          style: { stroke: "#000", lineWidth: 2, lineDash: [4, 4] },
          text: {
            content: "Latest time",
            position: "end",
            offsetY: 35,
            offsetX: -15,
            style: {
              fill: "#000",
              fontSize: 12,
              textAlign: "center",
              textBaseline: "middle",
            },
          },
        },
      ],
      color: ({ name }: { name: string }) => {
        switch (name) {
          case "Real Price":
            return "purple";
          case "Predicted Price":
            return "red";
          case "Lower Bound":
            return "blue";
          case "Upper Bound":
            return "green";
          default:
            return boundColor;
        }
      },
      area: [
        {
          name: "95% Confidence Interval",
          xField: "date",
          yField: ["lower", "upper"],
          data:
            fetchedData?.data?.prediction?.map(c => ({
              date: c.date,
              lower: c.lower,
              upper: c.upper,
            })) || [],
          areaStyle: { fill: boundColor, opacity: 0.2 },
        },
      ],
      smooth: true,
    }),
    [combinedData, yMin, yMax, storedTimes, fetchedData?.data?.prediction, currentCurrency],
  );

  const contentDom = useMemo(
    () => (
      <div className="flex flex-col gap-2">
        <Line {...(chartConfig as any)} />
        <LastUpdated dateTime={fetchTime?.toDate() ?? new Date()} />
      </div>
    ),
    [fetchTime, chartConfig],
  );

  return (
    <MyCard
      className="h-full"
      title={<Heading title={t("Price_Forecast")} />}
      extra={
        <Select
          className="w-[80px]"
          defaultValue={duration}
          onChange={setDuration}
          options={durations.map(duration => ({ label: duration, value: duration }))}
        />
      }
    >
      {isLoading ? (
        <div className="relative w-full h-full">
          <div className="absolute top-0 bottom-0 h-full z-[1000] inset-0 backdrop-blur-md bg-white/30" />
          <div className="absolute top-0 bottom-0 h-full z-[1000] inset-0">
            <Space className="w-full h-full justify-center items-center" direction="vertical" size={18}>
              <div className="w-full flex flex-col justify-center items-center gap-y-2">
                <Spin />
                <Typography.Text style={{ color: "#7A4EE7" }} className="text-center">
                  {t("Our_AI")}
                </Typography.Text>
              </div>
            </Space>
          </div>
          {contentDom}
        </div>
      ) : (
        contentDom
      )}
    </MyCard>
  );
};

export default PriceForecast;
